/**
 * IndustryCityMarketingPage
 *
 * Chassis for city × industry MARKETING-intent landing pages.
 * Distinct from:
 *   - CityServicePage (single-service city pages — SEO / web design / Maps)
 *   - IndustryMarketingPage (statewide industry hubs)
 *   - SpringsLandingPage (Springs SEO-intent micro-landings)
 *
 * Powers URLs like:
 *   /hvac-marketing-colorado-springs
 *   /plumbing-marketing-denver
 *
 * Each page presents the full marketing mix (SEO + Maps + Web + Ads + GEO AI)
 * scoped to one city × one industry, with hand-written copy per route.
 */
import { Link } from "@tanstack/react-router";
import { buildLocalBusinessSchema, SCHEMA_LOCALBUSINESS_ID } from "@/lib/entitySchema";
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  MapPin,
  Search,
  Globe,
  Target,
  Bot,
  Megaphone,
  Star,
  TrendingUp,
  AlertTriangle,
  Heart,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { LazyMount } from "@/components/LazyMount";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LastUpdated } from "@/components/LastUpdated";
import { ogImageFor, ogMetaTags, twitterTextTags, type OgCityKey } from "@/lib/ogImages";
import { getPageLastUpdated } from "@/lib/lastUpdatedRegistry";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";

const SITE_URL = "https://coloradowebimpressions.com";
const PHONE = "+1-719-419-3935";
const PHONE_DISPLAY = "(719) 419-3935";
const HQ_ADDRESS = {
  streetAddress: "3210 E Woodmen Rd STE 110",
  addressLocality: "Colorado Springs",
  addressRegion: "CO",
  postalCode: "80920",
  addressCountry: "US",
};
const HQ_GEO = { latitude: 38.9425, longitude: -104.7558 };
const GBP_CID_URL = "https://www.google.com/maps?cid=14271876770999369449";

export type ICMFaq = { q: string; a: string };
export type ICMLink = { to: string; label: string; description?: string };

export type IndustryCityMarketingConfig = {
  /** URL slug — full path is /{slug}. */
  slug: string;
  /** Industry display name. e.g. "HVAC". */
  industry: string;
  /** Lowercase variant for body copy. e.g. "hvac". */
  industryLower: string;
  /** City display name. e.g. "Colorado Springs". */
  city: string;
  /** City slug (for OG image lookup + cross-links). e.g. "colorado-springs". */
  citySlug: OgCityKey;
  /** City lat/lng (for areaServed schema). */
  cityGeo: { lat: number; lng: number };
  /** ZIP codes covered by this page. Drives schema areaServed. */
  zips: string[];
  /** Named neighborhoods / subdivisions / corridors in this city. */
  neighborhoods: string[];
  /** Nearby cities for cross-link block. */
  nearbyCities?: string[];

  /** SEO meta. Hand-write each — never templated. */
  metaTitle: string;
  metaDescription: string;
  /** H1 split into lead + gold-highlighted span. */
  h1: { lead: string; gold: string; trail?: string };
  /** Hero eyebrow tag. */
  eyebrow: string;
  /** Hero description paragraph (1–2 sentences). */
  heroDescription: string;
  /** Opening paragraph below the hero. Renders as speakable-intro. */
  intro: string;

  /** 3 pain points specific to this industry in this city. */
  painPoints: [string, string, string];

  /** Per-service one-line description scoped to this city × industry. */
  services: {
    seo: string;
    googleMaps: string;
    webDesign: string;
    ppc: string;
    geoAi: string;
    socialMedia: string;
  };

  /** Local trust strip. */
  trustPoints: [string, string, string, string];

  /** Optional local testimonial. */
  testimonial?: { quote: string; role: string; location: string };

  /** 5–8 FAQs — feed FAQPage schema. */
  faqs: ICMFaq[];

  /** Internal cross-links shown in footer grid. */
  related: ICMLink[];

  /** ISO date override for "Last updated" stamp + dateModified. */
  lastUpdated?: string;

  /** Show pricing package blocks directly below the hero. */
  showPricing?: boolean;
};

// =========================================================================
// Head builder
// =========================================================================

export function buildIndustryCityMarketingHead(c: IndustryCityMarketingConfig) {
  const canonical = `${SITE_URL}/${c.slug}`;
  const lastUpdated = c.lastUpdated ?? getPageLastUpdated(`/${c.slug}`);
  const ogImage = `${SITE_URL}${ogImageFor({ city: c.citySlug })}`;

  const areaServed: Array<Record<string, unknown>> = [
    {
      "@type": "City",
      name: c.city,
      containedInPlace: { "@type": "State", name: "Colorado" },
    },
    ...c.neighborhoods.map((n) => ({
      "@type": "Place",
      name: `${n}, ${c.city}, CO`,
    })),
    ...c.zips.map((z) => ({
      "@type": "PostalCodeArea",
      postalCode: z,
      addressRegion: "CO",
      addressCountry: "US",
    })),
  ];

  const localBusiness = {
    ...buildLocalBusinessSchema([c.city]),
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": SCHEMA_LOCALBUSINESS_ID,
    parentOrganization: { "@id": `${SITE_URL}/#org` },
    name: "Colorado Web Impressions",
    description: c.heroDescription,
    url: SITE_URL,
    dateModified: lastUpdated,
    telephone: PHONE,
    priceRange: "$$",
    address: { "@type": "PostalAddress", ...HQ_ADDRESS },
    areaServed,
    serviceArea: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: c.cityGeo.lat,
        longitude: c.cityGeo.lng,
      },
      geoRadius: "40000",
    },
    image: ogImage,
    sameAs: [GBP_CID_URL],
  };

  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${c.industry} Marketing in ${c.city}`,
    serviceType: `${c.industry} marketing & SEO`,
    description: c.heroDescription,
    provider: {
      "@type": "LocalBusiness",
      "@id": SCHEMA_LOCALBUSINESS_ID,
      name: "Colorado Web Impressions",
    },
    areaServed,
    url: canonical,
    dateModified: lastUpdated,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `${c.industry} marketing services in ${c.city}`,
      itemListElement: [
        "SEO",
        "Google Maps optimization",
        "Web design",
        "Google Ads / PPC",
        "GEO AI visibility",
        "Social media",
      ].map((s) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: `${s} for ${c.industryLower} in ${c.city}`,
          areaServed: { "@type": "City", name: c.city },
        },
      })),
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${canonical}#faq`,
    mainEntity: c.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "Industries",
        item: `${SITE_URL}/industries`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: `${c.industry} Marketing`,
        item: `${SITE_URL}/${c.industryLower.replace(/\s+/g, "-")}-marketing-colorado`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: c.city,
        item: canonical,
      },
    ],
  };

  const webPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: c.metaTitle,
    description: c.metaDescription,
    inLanguage: "en-US",
    isPartOf: { "@id": `${SITE_URL}/#org` },
    about: { "@id": `${canonical}#localbusiness` },
    author: { "@id": `${SITE_URL}/meet-chris#chris-heidlebaugh` },
    publisher: { "@id": `${SITE_URL}/#org` },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".speakable-intro", ".faq-answer", "[data-speakable]"],
    },
  };

  return {
    meta: [
      { title: c.metaTitle },
      { name: "description", content: c.metaDescription },
      { property: "og:title", content: c.metaTitle },
      { property: "og:description", content: c.metaDescription },
      { property: "og:url", content: canonical },
      { property: "og:type", content: "website" },
      ...ogMetaTags(ogImage),
      ...twitterTextTags({ title: c.metaTitle, description: c.metaDescription }),
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localBusiness) },
      { type: "application/ld+json", children: JSON.stringify(service) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
      { type: "application/ld+json", children: JSON.stringify(webPage) },
    ],
  };
}

// =========================================================================
// Component
// =========================================================================

export function IndustryCityMarketingPage({ config }: { config: IndustryCityMarketingConfig }) {
  const c = config;
  const stats = [
    { value: "3–5×", label: "More inbound leads" },
    { value: "60–120d", label: "To Map-Pack movement" },
    { value: "$1,650/mo", label: "Flat marketing — no contract" },
    { value: "97%", label: "Client retention" },
  ];

  const serviceCards = [
    { icon: Search, title: "SEO", desc: c.services.seo, to: "/local-seo" },
    { icon: MapPin, title: "Google Maps", desc: c.services.googleMaps, to: "/google-maps" },
    { icon: Globe, title: "Web Design", desc: c.services.webDesign, to: "/web-design" },
    { icon: Target, title: "Google Ads / PPC", desc: c.services.ppc, to: "/google-ads-ppc" },
    { icon: Bot, title: "GEO AI", desc: c.services.geoAi, to: "/geo-ai" },
    { icon: Megaphone, title: "Social Media", desc: c.services.socialMedia, to: "/digital-marketing" },
  ];

  return (
    <SiteLayout>
      <PageHero
        eyebrow={c.eyebrow}
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Industries", to: "/industries" },
          { label: `${c.industry} Marketing`, to: `/${c.industryLower.replace(/\s+/g, "-")}-marketing-colorado` },
          { label: c.city },
        ]}
        title={
          <>
            {c.h1.lead}{" "}
            <span className="text-gradient-gold">{c.h1.gold}</span>
            {c.h1.trail ? ` ${c.h1.trail}` : ""}
          </>
        }
        description={c.heroDescription}
        belowTitle={<LastUpdated date={c.lastUpdated ?? getPageLastUpdated(`/${c.slug}`)} />}
        hideTrustSignals
      >
        <div className="flex items-center justify-center gap-2 text-sm text-brand-cream/90">
          <Heart className="h-4 w-4 text-brand-gold" />
          <span>
            <strong className="font-semibold text-white">Talk to the owner directly</strong> — not a salesperson.
          </span>
        </div>
        <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a free {c.city} {c.industry} marketing plan
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href={`tel:${PHONE}`}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-brand-gold/40 px-7 py-4 text-base font-semibold text-brand-cream hover:bg-brand-gold/10"
          >
            <Phone className="h-4 w-4" />
            {PHONE_DISPLAY}
          </a>
        </div>
      </PageHero>

      {c.showPricing && <PricingTeaserStrip />}

      {/* Intro / speakable */}
      <section className="bg-background">
        <div className="mx-auto max-w-4xl px-4 py-16">
          <p className="speakable-intro text-lg leading-relaxed text-foreground">
            {c.intro}
          </p>
        </div>
      </section>

      {/* Pain points */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 pb-16">
          <div className="mb-8 flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-brand-gold" />
            <h2 className="text-2xl font-bold text-foreground">
              What we hear from {c.city} {c.industry} owners
            </h2>
          </div>
          <ul className="grid gap-4 md:grid-cols-3">
            {c.painPoints.map((p, i) => (
              <li
                key={i}
                className="rounded-lg border border-border bg-card p-5 text-sm leading-relaxed text-foreground/85 shadow-sm"
              >
                {p}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 6-service grid */}
      <section className="bg-background">
        <div className="mx-auto max-w-6xl px-4 pb-20">
          <h2 className="mb-3 text-3xl font-bold text-foreground">
            The full marketing stack for {c.city} {c.industry} companies
          </h2>
          <p className="mb-10 max-w-3xl text-foreground/75">
            Six channels, one owner-led team. Pick what you need — start with the
            one that fixes your biggest leak this quarter.
          </p>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {serviceCards.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.title}
                  to={s.to}
                  className="group rounded-lg border border-border bg-card p-6 shadow-sm transition hover:border-brand-gold/60 hover:shadow-md"
                >
                  <Icon className="mb-3 h-7 w-7 text-brand-gold" />
                  <h3 className="mb-2 text-lg font-bold text-foreground">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-foreground/75">{s.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-gold">
                    Learn more
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats — intentionally dark accent strip */}
      <section className="bg-gradient-dark py-12 text-brand-cream">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-6 px-4 md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-3xl font-bold text-brand-gold md:text-4xl">{s.value}</div>
              <div className="mt-1 text-xs uppercase tracking-wide text-brand-cream/80 md:text-sm">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-16">
          <div className="mb-6 flex items-center gap-3">
            <TrendingUp className="h-6 w-6 text-brand-gold" />
            <h2 className="text-2xl font-bold text-foreground">
              Why {c.city} {c.industry} owners choose us
            </h2>
          </div>
          <ul className="grid gap-3 md:grid-cols-2">
            {c.trustPoints.map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-foreground/85">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-gold" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Coverage block */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 pb-16">
          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-brand-gold" />
              <h2 className="text-xl font-bold text-foreground">{c.city} coverage</h2>
            </div>
            <p className="mb-4 text-sm text-foreground/75">
              We build dedicated landing copy and Map-Pack signals for these {c.city} neighborhoods and ZIP codes:
            </p>
            <div className="mb-3 flex flex-wrap gap-2">
              {c.neighborhoods.map((n) => (
                <span
                  key={n}
                  className="rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-medium text-foreground"
                >
                  {n}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {c.zips.map((z) => (
                <span
                  key={z}
                  className="rounded-full border border-border bg-muted px-2.5 py-1 font-mono text-xs text-foreground/70"
                >
                  {z}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {c.testimonial && (
        <section className="bg-background">
          <div className="mx-auto max-w-3xl px-4 pb-16">
            <figure className="rounded-lg border border-border bg-card p-8 shadow-sm">
              <div className="mb-3 flex gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4 fill-brand-gold text-brand-gold" />
                ))}
              </div>
              <blockquote className="text-lg italic leading-relaxed text-foreground">
                "{c.testimonial.quote}"
              </blockquote>
              <figcaption className="mt-4 text-sm text-foreground/70">
                — {c.testimonial.role}, {c.testimonial.location}
              </figcaption>
            </figure>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-background">
        <div className="mx-auto max-w-3xl px-4 pb-20">
          <h2 className="mb-8 text-3xl font-bold text-foreground">
            {c.city} {c.industry} marketing FAQs
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {c.faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-border">
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:text-brand-gold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="faq-answer text-foreground/80">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Related links */}
      {c.related.length > 0 && (
        <section className="border-t border-border bg-section-alt py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="mb-6 text-2xl font-bold text-foreground">Related pages</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {c.related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="group rounded-lg border border-border bg-card p-5 shadow-sm transition hover:border-brand-gold/60 hover:shadow-md"
                >
                  <div className="mb-1 font-semibold text-foreground group-hover:text-brand-gold">
                    {r.label}
                  </div>
                  {r.description && (
                    <p className="text-sm text-foreground/70">{r.description}</p>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* Final CTA */}
      <LazyMount>
        <section className="bg-gradient-to-b from-brand-black to-brand-black/80 py-20">
          <div className="mx-auto max-w-3xl px-4 text-center">
            <h2 className="text-3xl font-bold text-white md:text-4xl">
              Ready to be the {c.industry} company {c.city} calls first?
            </h2>
            <p className="mt-4 text-brand-cream/85">
              Get a free marketing plan tailored to your {c.city} service area, your competitors, and your capacity. No salespeople, no fluff.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-8 py-4 font-bold text-brand-black shadow-gold hover:-translate-y-0.5"
              >
                Get my free plan
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`tel:${PHONE}`}
                className="inline-flex items-center gap-2 rounded-md border border-brand-gold/40 px-8 py-4 font-semibold text-brand-cream hover:bg-brand-gold/10"
              >
                <Phone className="h-4 w-4" />
                {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </section>
      </LazyMount>
    </SiteLayout>
  );
}
